//@flow

import {
  listStoreLocationsService,
  getStoreLocationService,
  createStoreLocationService,
  deleteStoreLocationService,
  updateStoreLocationService,
} from '../promises/location';
import {useEffect, useState} from 'react';

import type {
  CreateStoreLocationRequest,
  UpdateStoreLocationRequest,
} from '../promises/location';
import type {StoreLocation} from '../../../entities/store-location';
import {useToastActions} from '../../toast-alert/utils';
import {useFeatureSpec} from '../../../utils/component';

export function useStoreLocations() {
  const [locations, setLocations] = useState<StoreLocation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    listStoreLocationsService()
      .then(locations => {
        setLocations(locations);
      })
      .catch(error => {
        console.error('Error fetching locations:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const refreshLocations = () => {
    setLoading(true);
    listStoreLocationsService()
      .then(locations => {
        setLocations(locations);
      })
      .catch(error => {
        console.error('Error fetching locations:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    refreshLocations,
    locations,
    loading,
  };
}

export function useStoreLocationActions() {
  const [loading, setLoading] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);

  const featureSpec = useFeatureSpec('STORE_LOCATION');

  const {showToast} = useToastActions();

  const createStoreLocation = async (location: CreateStoreLocationRequest) => {
    setLoading(true);
    try {
      const createdLocation = await createStoreLocationService(location);
      return createdLocation;
    } catch (err) {
      const errorName = err?.error.error;

      if (errorName === 'MaxStoreLocationsLimitExceeded') {
        showToast({
          type: 'danger',
          title: 'Oops!',
          message: `Solo puedes tener ${featureSpec?.limit ||
            '2'} ubicaciones por tienda.`,
        });

        return;
      }

      showToast({
        type: 'danger',
        title: 'Error',
        message: 'Ocurrió un error al crear la ubicación de la tienda.',
      });

      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStoreLocation = async (
    locationId: string,
    location: UpdateStoreLocationRequest
  ) => {
    setLoading(true);
    try {
      const updatedLocation = await updateStoreLocationService(
        locationId,
        location
      );
      return updatedLocation;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteStoreLocation = async (locationId: string) => {
    setLoading(true);
    try {
      await deleteStoreLocationService(locationId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    createStoreLocation,
    updateStoreLocation,
    deleteStoreLocation,
    loading,
    error,
  };
}

export function useStoreLocation(id?: string | null) {
  const [location, setLocation] = useState<StoreLocation | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getStoreLocationService(id)
      .then(location => {
        setLocation(location);
      })
      .catch(error => {
        console.error('Error fetching location:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  return {
    location,
    loading,
  };
}
